import type { RouteLocationResolved } from 'vue-router'

import { ROUTE_NAMES } from '@/router/constants'
import safeURLParse from '@/utils/safeURLParse'

import type { GoogleSearchAPIResult, SearchGameResult, SearchGroupResult, SearchResults } from './types'

export const doSearch = async ({
    searchID,
    apiKey,
    locale,
    lang,
    q,
    resolveRoute,
}: {
    searchID: string
    apiKey: string
    locale: string
    lang: string
    q: string
    resolveRoute: (link: string) => RouteLocationResolved
}) => {
    // Doc: https://developers.google.com/custom-search/v1/reference/rest/v1/cse/list#request
    const params = new URLSearchParams({
        // custom search ID
        cx: searchID,
        // API key
        key: apiKey,
        q,
        num: '10',
        // interface lang
        hl: locale,
        // restrict results only on this lang
        lr: `lang_${lang}`,
        // user geolocation
        // gl: ...
        // siteSearch: ...
        // siteSearchFilter: 'i' | 'e'
    })
    const res = await fetch(`https://customsearch.googleapis.com/customsearch/v1?${params.toString()}`)
    if (!res.ok) {
        console.error('Search failed', await res.text())
        throw new Error('Search failed')
    }
    const data = (await res.json()) as GoogleSearchAPIResult

    const searchResult: SearchResults = {
        result: [],
        totalItems: 0,
        queryTerm: '',
    }
    const findByHru = (searchedItem: SearchGameResult | SearchGroupResult, hru: string) => {
        if (typeof searchedItem === 'object' && 'hru' in searchedItem) {
            return searchedItem.hru === hru
        }
        return false
    }
    const getHruName = (hru: string | undefined) => {
        if (!hru) {
            return undefined
        }
        const title = hru.split('-').map((item) => item.charAt(0).toUpperCase() + item.slice(1))
        return title.join(' ').replace(/\s+/g, ' ')
    }
    ;(data.items || []).forEach((item) => {
        const linkPath = safeURLParse(item.link)?.pathname
        if (!linkPath) {
            return
        }
        const route = resolveRoute(linkPath)
        if (route.name === ROUTE_NAMES.GAME_PAGE) {
            const { listitem, metatags } = item.pagemap || {}
            const metaImage = metatags?.[0]['og:image']
            const name = listitem?.[listitem?.length - 1]?.name || getHruName(route.params.game as string)
            const webapplicationMeta = item.pagemap?.webapplication?.[0]
            const image = webapplicationMeta?.image || metaImage
            if (name && image) {
                searchResult.result.push({
                    routeName: route.name,
                    gameHru: route.params.game as string,
                    image,
                    name,
                    htmlTitle: item.htmlTitle,
                })
            }
        } else if (
            route.name === ROUTE_NAMES.CATEGORY ||
            route.name === ROUTE_NAMES.TAG ||
            route.name === ROUTE_NAMES.SERIES
        ) {
            const { listitem } = item.pagemap || {}
            const name = listitem?.[listitem?.length - 1]?.name || item.title?.split('|')[0].trim()
            if (name) {
                const hru = route.params.hru as string
                if (!searchResult.result.find((i) => findByHru(i, hru))) {
                    searchResult.result.push({
                        routeName: route.name,
                        hru,
                        name,
                        htmlTitle: item.htmlTitle,
                    })
                }
            }
        }
    })
    searchResult.totalItems = data.searchInformation.totalResults
    searchResult.queryTerm = q
    return searchResult
}
